import {
  ClientProviderEvents,
  GenericEventEmitter,
  type JsonValue,
  type ResolutionDetails,
  type EvaluationContext,
  type Logger
} from '@openfeature/core';
import EventEmitter from 'eventemitter3';
import type { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import type { OrgResolvedFlagsType } from '@bett3r-dev/teselly-domain';
import type { FeatureFlagClientProvider } from './types';
import type { TelemetrySink } from './telemetry';

/**
 * `@openfeature/core`'s `GenericEventEmitter` is abstract — it declares an
 * abstract `eventEmitter` member that each SDK flavor supplies. The web-sdk's
 * `OpenFeatureEventEmitter` provides it via the `eventemitter3` package. This
 * concrete subclass does the same so the package stays SDK-agnostic while the
 * emitter is fully functional.
 */
class TessellyEventEmitter extends GenericEventEmitter<ClientProviderEvents> {
  protected readonly eventEmitter = new EventEmitter();
}


export class TessellyFeatureFlagProvider implements FeatureFlagClientProvider {
  readonly metadata = { name: 'teselly-feature-flags' } as const;
  readonly events = new TessellyEventEmitter();
  readonly runsOn = 'client' as const;

  private flags: Map<string, boolean> = new Map();
  private variants: Map<string, string> = new Map();
  private telemetryModes: Map<string, string> = new Map();
  private trackedExposures: Set<string> = new Set();
  private subscription: { close: () => void } | null = null;
  /**
   * True while a `refetchFlags()` is in flight — i.e. the current `flags` Map may
   * still hold the pre-auth/public snapshot and must NOT be trusted for routing
   * decisions until the account-scoped snapshot lands. Consumed by
   * `useFeatureFlagsReady`. Cleared the moment the next snapshot is applied.
   */
  private isStale = false;
  private backend: ClientLibraryType;
  private telemetry: TelemetrySink;

  // Bounded retry for the post-login account-scoped flag refetch. A transient
  // `/org-resolved-flags` failure right after login (403 while membership is
  // still projecting) must NOT leave the provider permanently `isStale` — that
  // hangs the home-redirect loader until a manual page refresh. We retry the
  // query until it lands, then degrade to `markReady()` so the app never hangs.
  private refetchTimer: ReturnType<typeof setTimeout> | null = null;
  private refetchAttempts = 0;
  private static readonly REFETCH_MAX_ATTEMPTS = 20;
  private static readonly REFETCH_RETRY_MS = 750;

  constructor( deps: { backend: ClientLibraryType; telemetry: TelemetrySink }) {
    this.backend = deps.backend;
    this.telemetry = deps.telemetry;
  }

  /**
   * Load the initial flag set via an HTTP query BEFORE the first route render,
   * then subscribe for live updates. `setProviderAndWait` awaits this, so
   * `isReady` is gated on flags actually being loaded.
   *
   * Realtime subscriptions deliver only *subsequent* updates, not an initial
   * snapshot (the project-wide query-first + subscribe pattern), so relying on
   * a `_snapshot` callback left the flags empty — `FlaggedRoute` then read the
   * default `false` and redirected flag-gated routes to `/` before any update
   * arrived (the bug behind E2E `page.goto('/inventory')` bouncing to home).
   * The query is best-effort: a failure degrades to default values rather than
   * hanging the app, and live updates still arrive via the subscription.
   */
  async initialize(): Promise<void> {
    try {
      const initial = await this.backend.queries.OrgResolvedFlags({});
      if ( initial ) this.applyFlagData( initial as unknown as OrgResolvedFlagsType );
    } catch {
      // `/org-resolved-flags` is auth-gated + account-scoped, so it throws for
      // an anonymous visitor (the Authentication screen). Fall back to the
      // public pre-auth allow-list so flags like `auth-register` resolve
      // pre-login. Once a session is established, ProvideAuthMemberships fires
      // refetchFlags() and the account-scoped set takes over via the
      // subscription.
      await this.loadPublicFlags();
    }
    this.setupSubscription();
  }

  /**
   * Best-effort load of the public, unauthenticated pre-auth flag set. Used as
   * a fallback when the authenticated `/org-resolved-flags` query is
   * unavailable (no session). Returns only the curated server allow-list.
   */
  private async loadPublicFlags(): Promise<void> {
    try {
      const publicFlags = await this.backend.get.public_flags();
      if ( publicFlags ) this.applyFlagData( publicFlags as unknown as OrgResolvedFlagsType );
    } catch {
      // Degrade to defaults; live updates still arrive via the subscription.
    }
  }

  private setupSubscription(): void {
    this.subscription = this.backend.subscriptions.OrgResolvedFlags({ _snapshot: true }, ( change ) => {
      if ( change.type === 'snapshot' ) {
        const item = change.data.items?.[0];
        // Only clear staleness once real flag data is applied. An empty snapshot
        // (projection lag right after login) must keep us stale so the home
        // redirect waits for the subsequent insert/update rather than routing
        // off the pre-auth set.
        if ( item ) {
          this.applyFlagData( item as OrgResolvedFlagsType );
          this.markReady();
        }
        return;
      }
      if ( change.type === 'insert' || change.type === 'update' ) {
        this.applyFlagData( change.data as OrgResolvedFlagsType );
        this.markReady();
      }
    });
  }

  /**
   * Clears the post-refetch stale flag and notifies subscribers (e.g.
   * `useFeatureFlagsReady`). No-op when not stale, so ordinary live updates and
   * the initial load don't churn readiness state.
   */
  private markReady(): void {
    if ( !this.isStale ) return;
    this.isStale = false;
    this.events.emit( ClientProviderEvents.Ready );
  }

  private applyFlagData( data: OrgResolvedFlagsType ): void {
    const changedKeys: string[] = [];

    // Detect changed boolean flags
    const newFlags = data.flags ?? {};
    const allFlagKeys = new Set([ ...this.flags.keys(), ...Object.keys( newFlags ) ]);
    for ( const key of allFlagKeys ) {
      const oldValue = this.flags.get( key );
      const newValue = newFlags[ key ];
      if ( oldValue !== newValue ) {
        changedKeys.push( key );
      }
    }

    // Detect changed variants
    const newVariants = data.variants ?? {};
    const allVariantKeys = new Set([ ...this.variants.keys(), ...Object.keys( newVariants ) ]);
    for ( const key of allVariantKeys ) {
      const oldValue = this.variants.get( key );
      const newValue = newVariants[ key ];
      if ( oldValue !== newValue && !changedKeys.includes( key )) {
        changedKeys.push( key );
      }
    }

    // Update internal Maps
    this.flags.clear();
    for ( const [ key, value ] of Object.entries( newFlags )) {
      this.flags.set( key, value as boolean );
    }

    this.variants.clear();
    for ( const [ key, value ] of Object.entries( newVariants )) {
      this.variants.set( key, value as string );
    }

    const newTelemetry = data.telemetry ?? {};
    this.telemetryModes.clear();
    for ( const [ key, value ] of Object.entries( newTelemetry )) {
      this.telemetryModes.set( key, value as string );
    }

    // Reset exposure tracking when flag values change so new values get tracked
    if ( changedKeys.length > 0 ) {
      for ( const key of changedKeys ) {
        this.clearTrackedExposuresForFlag( key );
      }
    }

    // Emit configuration changed event if any flags changed
    if ( changedKeys.length > 0 ) {
      this.events.emit( ClientProviderEvents.ConfigurationChanged, {
        flagsChanged: changedKeys
      });
    }
  }

  resolveBooleanEvaluation( flagKey: string, defaultValue: boolean, _context: EvaluationContext, _logger: Logger ): ResolutionDetails<boolean> {
    const value = this.flags.get( flagKey );
    if ( value === undefined ) {
      return { value: defaultValue, reason: 'DEFAULT' };
    }
    this.trackExposure( flagKey, value );
    return { value, reason: 'STATIC' };
  }

  resolveStringEvaluation( flagKey: string, defaultValue: string, _context: EvaluationContext, _logger: Logger ): ResolutionDetails<string> {
    const variant = this.variants.get( flagKey );
    if ( variant === undefined ) {
      return { value: defaultValue, reason: 'DEFAULT' };
    }
    this.trackExposure( flagKey, true, variant );
    return { value: variant, variant, reason: 'STATIC' };
  }

  resolveNumberEvaluation( _flagKey: string, defaultValue: number, _context: EvaluationContext, _logger: Logger ): ResolutionDetails<number> {
    return { value: defaultValue, reason: 'DEFAULT' };
  }

  resolveObjectEvaluation<T extends JsonValue>( _flagKey: string, defaultValue: T, _context: EvaluationContext, _logger: Logger ): ResolutionDetails<T> {
    return { value: defaultValue, reason: 'DEFAULT' };
  }

  private trackExposure( flagKey: string, value: boolean | string, variant?: string ): void {
    const telemetryMode = this.telemetryModes.get( flagKey ) ?? 'none';
    if ( telemetryMode === 'none' ) return;

    if ( telemetryMode === 'positive' && value !== true ) return;
    if ( telemetryMode === 'negative' && value !== false ) return;
    // telemetryMode === 'all' always proceeds

    // Deduplicate: only track once per flag key + value combination
    const dedupeKey = `${flagKey}:${String( value )}`;
    if ( this.trackedExposures.has( dedupeKey )) return;
    this.trackedExposures.add( dedupeKey );

    const flagVariant = variant ?? this.variants.get( flagKey );
    this.telemetry.trackFlagExposure({ flagKey, value, variant: flagVariant });
  }

  private clearTrackedExposuresForFlag( flagKey: string ): void {
    for ( const key of this.trackedExposures ) {
      if ( key.startsWith( `${flagKey}:` )) {
        this.trackedExposures.delete( key );
      }
    }
  }

  async onClose(): Promise<void> {
    if ( this.refetchTimer ) {
      clearTimeout( this.refetchTimer );
      this.refetchTimer = null;
    }
    if ( this.subscription ) {
      this.subscription.close();
      this.subscription = null;
    }
  }

  /**
   * Triggers a re-fetch of the flags by closing and re-opening the subscription.
   * Called externally by ProvideAuthMemberships when memberships change.
   */
  refetchFlags(): void {
    // Mark the current set stale BEFORE re-subscribing: until the account-scoped
    // snapshot arrives, `flags` still holds the pre-auth/public set and routing
    // off it sends entitled users to /not-found (the login home-redirect race).
    this.isStale = true;
    this.events.emit( ClientProviderEvents.Stale );
    // Reset any in-flight retry loop from a prior refetch so this fresh refetch
    // starts its own bounded attempt budget.
    if ( this.refetchTimer ) {
      clearTimeout( this.refetchTimer );
      this.refetchTimer = null;
    }
    this.refetchAttempts = 0;
    if ( this.subscription ) {
      this.subscription.close();
    }
    this.setupSubscription();
    // Query-first refresh. `setupSubscription()` only re-opens the WS, and realtime
    // subscriptions deliver only *subsequent* updates — their initial snapshot is
    // unreliable (in E2E it never arrives), and a pre-provisioned account produces
    // no post-subscribe insert/update either. So a re-subscribe alone never swaps in
    // the account-scoped flags post-login: the provider stays permanently `isStale`
    // on the pre-auth/public set and `markReady()` is never reached. Re-issue the
    // authenticated HTTP query — which reliably carries the now-present session
    // cookie — and mark ready once it lands. Mirrors `initialize()`'s query-first load.
    void this.refetchAccountFlags();
  }

  /**
   * HTTP re-fetch of the account-scoped flag set, used by `refetchFlags()`. The
   * query is auth-gated; on failure (no session yet) we keep the current flags
   * and stay stale so the home redirect keeps waiting rather than routing off the
   * pre-auth set.
   */
  private async refetchAccountFlags(): Promise<void> {
    // A newer lifecycle (another refetch's success, or onClose) may have cleared
    // staleness while this attempt was scheduled — bail so we don't churn.
    if ( !this.isStale ) return;

    try {
      const data = await this.backend.queries.OrgResolvedFlags({});
      // Require an actual account-scoped row. A truthy-but-empty payload is
      // projection lag right after login (same rationale as the empty-snapshot
      // guard in `setupSubscription`): keep waiting rather than marking ready on
      // the pre-auth set.
      if ( data && Object.keys(( data as { flags?: Record<string, unknown> }).flags ?? {}).length > 0 ) {
        this.applyFlagData( data as unknown as OrgResolvedFlagsType );
        this.refetchAttempts = 0;
        this.markReady();
        return;
      }
    } catch {
      // No session resolved server-side yet (403 while membership projects), or a
      // transient gateway/network error — fall through to the bounded retry.
    }

    // Still no account-scoped flags. Retry with a fixed backoff so a transient
    // post-login failure doesn't strand the provider as permanently `isStale`
    // (which hangs the home-redirect loader until a manual refresh). After the
    // attempt budget is exhausted, degrade to `markReady()` so the app renders
    // with whatever flags we have instead of hanging forever.
    if ( !this.isStale ) return;
    if ( this.refetchAttempts < TessellyFeatureFlagProvider.REFETCH_MAX_ATTEMPTS ) {
      this.refetchAttempts += 1;
      this.refetchTimer = setTimeout(() => {
        this.refetchTimer = null;
        void this.refetchAccountFlags();
      }, TessellyFeatureFlagProvider.REFETCH_RETRY_MS );
    } else {
      this.refetchAttempts = 0;
      this.markReady();
    }
  }

  /**
   * True while a `refetchFlags()` is in flight (post-login). Consumers that route
   * off the flag set — e.g. the home redirect — must wait for this to be false.
   */
  isStaleFlags(): boolean {
    return this.isStale;
  }

  /**
   * Returns the full flags Map for useActiveFeatureFlags.
   */
  getAllFlags(): Map<string, boolean> {
    return this.flags;
  }
}
